/**
 * Focus trap utility — używany przez search/cart/quickview/filter/mobile drawers.
 *
 * Auto-wires na elementach po `id` (poniżej `drawers[]`), włącza/wyłącza trap
 * obserwując zmianę `aria-hidden` na danym kontenerze.
 *
 * Manual usage:
 *   const trap = createFocusTrap(panelElement);
 *   trap.activate(); ...; trap.deactivate();
 */

export function createFocusTrap(container) {
  if (!container) return { activate: () => {}, deactivate: () => {} };

  let prevFocus = null;
  let handler = null;
  let escHandler = null;

  function getFocusable() {
    return Array.from(container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((el) => {
      // Widoczne (offsetParent działa dla większości; fallback dla position:fixed).
      if (el.offsetParent !== null) return true;
      const style = window.getComputedStyle(el);
      return style.position === 'fixed' && style.visibility !== 'hidden' && style.display !== 'none';
    });
  }

  function activate() {
    prevFocus = document.activeElement;
    const focusables = getFocusable();
    if (focusables.length) {
      // Lekkie opóźnienie — gdy drawer ma transition opacity/transform,
      // focus przed paint potrafi nie zadziałać w niektórych browserach.
      requestAnimationFrame(() => {
        const f = getFocusable();
        if (f.length) f[0].focus();
      });
    }

    handler = (e) => {
      if (e.key !== 'Tab') return;
      const f = getFocusable();
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', handler);

    // Escape — emituje custom event 'kb:drawer-close', drawery same nasłuchują/zamykają.
    escHandler = (e) => {
      if (e.key === 'Escape') {
        container.dispatchEvent(new CustomEvent('kb:drawer-close', { bubbles: true }));
      }
    };
    container.addEventListener('keydown', escHandler);
  }

  function deactivate() {
    if (handler) {
      container.removeEventListener('keydown', handler);
      handler = null;
    }
    if (escHandler) {
      container.removeEventListener('keydown', escHandler);
      escHandler = null;
    }
    if (prevFocus && typeof prevFocus.focus === 'function' && document.contains(prevFocus)) {
      prevFocus.focus();
    }
    prevFocus = null;
  }

  return { activate, deactivate };
}

// Auto-wire dla naszych drawerów — używamy MutationObserver na aria-hidden.
const drawers = ['search-drawer', 'cart-drawer', 'qv-drawer', 'filter-drawer', 'mobile-menu'];

function init() {
  drawers.forEach((id) => {
    const el = document.getElementById(id);
    if (!el || el.dataset.kbTrap === '1') return;
    el.dataset.kbTrap = '1';
    const trap = createFocusTrap(el);

    // Ustaw initial state w zależności od aria-hidden.
    const initiallyOpen = el.getAttribute('aria-hidden') === 'false';
    if (initiallyOpen) trap.activate();

    const observer = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.attributeName === 'aria-hidden') {
          const isOpen = el.getAttribute('aria-hidden') === 'false';
          if (isOpen) trap.activate();
          else trap.deactivate();
        }
      }
    });
    observer.observe(el, { attributes: true, attributeFilter: ['aria-hidden'] });
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
